/**
 * Step by step — step stack (Figma node 1148:1834).
 *
 * In scroll mode a vertical Swiper reads wheel and swipe input and opens one card per
 * gesture, handing the page back past either end; the cards still animate with CSS
 * (docs/theme-architecture/sections/step-by-step.md).
 */

const STACK = {
  resizeDelay: 150,
  mobileQuery: '(max-width: 768px)',
  minTypeScale: 0.7,
  wheelGap: 200,
  inputRatio: 0.7,
};

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

class StepStack extends HTMLElement {
  connectedCallback() {
    // The theme editor re-inserts sections. Handlers are created once; a reconnect
    // only re-attaches the listeners disconnectedCallback took off.
    if (!this.initialised) this.#init();
    else if (this.bound) {
      this.#listen();
      this.#fit();
    }
  }

  #init(retry = true) {
    this.pin = this.querySelector('[data-step-pin]');
    this.body = this.querySelector('.step-by-step__body');
    this.track = this.querySelector('[data-step-track]');
    this.steps = Array.from(this.querySelectorAll('[data-step]'));

    // No panels usually means the children have not parsed yet. Falling through
    // to static would be unrecoverable — it sets data-ready, so the section's
    // failsafe would not fire either.
    if (!this.steps.length && retry) {
      requestAnimationFrame(() => this.#init(false));
      return;
    }

    this.initialised = true;

    // Liquid's count is a pre-paint estimate; the rendered panels are the real one.
    this.style.setProperty('--sbs-step-count', String(this.steps.length));

    if (!this.pin || !this.body || !this.track || this.steps.length < 2 || prefersReducedMotion()) {
      this.dataset.mode = 'static';
      this.#ready();
      return;
    }

    this.requestedMode = this.dataset.mode;
    this.toggles = this.steps.map((step) => step.querySelector('[data-step-toggle]'));
    this.activeIndex = -1;
    this.#setActive(0);
    this.#bind();
    this.#listen();
    this.#fit();
    this.#ready();
  }

  /**
   * Long copy can overflow the open panel. Falls back to data-fit="compact" (track
   * fills the screen, heading above it), then scaled type, then the static stack.
   */
  #fit() {
    // Natural open height: the tallest step's copy at full hairline length, which
    // only the static layout lays out unconstrained.
    this.dataset.mode = 'static';
    delete this.dataset.fit;
    this.style.removeProperty('--sbs-type-scale');
    const natural = Math.max(
      ...this.steps.map((step) => step.querySelector('.step__inner')?.offsetHeight || 0)
    );
    this.style.setProperty('--sbs-open-natural', `${Math.ceil(natural)}px`);

    this.dataset.mode = this.requestedMode;
    this.#fitMode();
    const sliding = this.#isSliding();
    this.toggleAttribute('data-strips', !sliding);
    if (sliding) this.#mountSwiper();
    else this.#unmountSwiper();
    this.#syncFocus();
  }

  #fitMode() {
    if (!this.#overflows()) return;

    const trackTop =
      this.track.getBoundingClientRect().top - this.pin.getBoundingClientRect().top;
    this.style.setProperty('--sbs-lead', `${Math.max(0, trackTop - this.#navOffset())}px`);
    this.dataset.fit = 'compact';
    if (!this.#overflows()) return;

    const scale = this.#typeScale();
    if (scale) {
      this.style.setProperty('--sbs-type-scale', String(scale));
      if (!this.#overflows()) return;
    }
    this.style.removeProperty('--sbs-type-scale');

    delete this.dataset.fit;
    this.dataset.mode = 'static';
  }

  /**
   * The largest scale, to 0.01, at which every step's copy fits. Smaller type
   * also wraps onto fewer lines, so the fit is searched, not computed.
   */
  #typeScale() {
    const fits = (scale) => {
      this.style.setProperty('--sbs-type-scale', String(scale));
      return !this.#overflows();
    };
    let low = STACK.minTypeScale;
    let high = 1;
    if (!fits(low)) return 0;
    while (high - low > 0.01) {
      const mid = (low + high) / 2;
      if (fits(mid)) low = mid;
      else high = mid;
    }
    return Math.floor(low * 100) / 100;
  }

  #overflows() {
    return this.steps.some((step) => {
      const content = step.querySelector('.step__content');
      const inner = step.querySelector('.step__inner');
      return (
        (content && content.scrollHeight > content.clientHeight + 1) ||
        (inner && inner.scrollHeight > inner.clientHeight + 1)
      );
    });
  }

  disconnectedCallback() {
    this.#unlisten();
    this.#unmountSwiper();
    clearTimeout(this.resizeTimer);
  }

  /** Releases the section's 3s failsafe (mirrors sections/hero.liquid). */
  #ready() {
    this.setAttribute('data-ready', '');
  }

  /** Creates every handler once; #listen and #unlisten only attach and detach them. */
  #bind() {
    this.bound = true;
    this.toggles.forEach((toggle, index) => {
      if (toggle) toggle.addEventListener('click', () => this.#show(index));
    });

    this.onKey = (event) => {
      if (event.key === 'Tab' && this.swiper && this.contains(event.target)) this.#onTab(event);
    };
    this.onWheel = () => {
      if (this.swiper && !this.swiper.params.mousewheel.releaseOnEdges) this.#holdEdges();
    };
    this.onResize = () => {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => this.#fit(), STACK.resizeDelay);
    };
    // A merchant selecting step 3 in the editor would otherwise still see step 1. The
    // editor scrolls to the block itself, so #reveal stays out of its way.
    this.onSelect = (event) => {
      const index = this.steps.indexOf(event.target.closest('[data-step]'));
      if (index < 0) return;
      this.editorSelect = true;
      this.#show(index);
      this.editorSelect = false;
    };

    // Copy height also changes without a window resize (fonts, reflow, edits). A
    // refit that lands on the same scale leaves the size unchanged, so no loop.
    this.copyObserver = new ResizeObserver(this.onResize);
  }

  #listen() {
    window.addEventListener('resize', this.onResize, { passive: true });
    window.addEventListener('keydown', this.onKey);
    document.addEventListener('shopify:block:select', this.onSelect);
    this.steps.forEach((step) => {
      const blocks = step.querySelector('.step__blocks');
      if (blocks) this.copyObserver.observe(blocks);
    });
  }

  #unlisten() {
    if (!this.bound) return;
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('keydown', this.onKey);
    document.removeEventListener('shopify:block:select', this.onSelect);
    this.copyObserver.disconnect();
  }

  /** Scroll mode, unless the merchant asked for click on mobile and this is mobile. */
  #isSliding() {
    if (this.dataset.mode !== 'scroll' || !window.Swiper) return false;
    if (this.dataset.mobileMode !== 'click') return true;
    return !window.matchMedia(STACK.mobileQuery).matches;
  }

  #navOffset() {
    return parseFloat(getComputedStyle(this).getPropertyValue('--sbs-nav-offset')) || 0;
  }

  /**
   * Swiper only reads the input: virtualTranslate keeps it from moving anything, and
   * each slide change sets the open card, which the CSS animates.
   */
  #mountSwiper() {
    if (this.swiper) {
      this.swiper.update();
      return;
    }
    const speed = parseFloat(getComputedStyle(this).getPropertyValue('--sbs-duration')) || 600;
    this.swiper = new window.Swiper(this.body, {
      direction: 'vertical',
      wrapperClass: 'step-by-step__track',
      slideClass: 'step',
      slidesPerView: 'auto',
      virtualTranslate: true,
      speed,
      initialSlide: this.activeIndex,
      resistanceRatio: 0,
      touchReleaseOnEdges: true,
      mousewheel: { releaseOnEdges: true, forceToAxis: true, thresholdTime: speed },
      // Swiper's a11y would label each step a slide, over the toggles' aria-expanded.
      a11y: { enabled: false },
      on: {
        slideChange: (swiper) => {
          this.#setActive(swiper.activeIndex);
          this.#holdEdges();
          if (!this.editorSelect) this.#reveal();
        },
        // Nothing Swiper moves ever fires transitionend, so it would stay animating
        // and ignore every later gesture.
        transitionStart: (swiper) => {
          clearTimeout(this.transitionTimer);
          this.transitionTimer = setTimeout(() => swiper.transitionEnd(), swiper.params.speed);
        },
      },
    });
    this.body.addEventListener('wheel', this.onWheel, { passive: true });
    this.#setInput(false);
    this.viewObserver = new IntersectionObserver(
      ([entry]) => this.#setInput(entry.intersectionRatio >= STACK.inputRatio),
      { rootMargin: `-${this.#navOffset()}px 0px 0px 0px`, threshold: [0, STACK.inputRatio, 1] }
    );
    this.viewObserver.observe(this.track);
  }

  /** A track mostly off screen leaves wheel and swipe to the page. */
  #setInput(on) {
    if (!this.swiper) return;
    this.swiper.allowTouchMove = on;
    if (on) this.swiper.mousewheel.enable();
    else this.swiper.mousewheel.disable();
  }

  /**
   * Brings the frame on screen below the nav, centred when it is shorter. Compact's
   * frame is taller than the screen, so there the track goes under the nav.
   */
  #reveal() {
    const nav = this.#navOffset();
    const rect = (this.dataset.fit === 'compact' ? this.track : this.pin).getBoundingClientRect();
    if (rect.top >= nav - 1 && rect.bottom <= window.innerHeight + 1) return;
    const centred = Math.max(0, (window.innerHeight - nav - rect.height) / 2);
    window.scrollTo({ top: window.scrollY + rect.top - nav - centred, behavior: 'smooth' });
  }

  /**
   * A trackpad keeps sending a gesture's momentum after it opens the first or last card,
   * and Swiper would hand that straight to the page. The page waits for a wheel pause.
   */
  #holdEdges() {
    const mousewheel = this.swiper?.params.mousewheel;
    if (!mousewheel) return;
    mousewheel.releaseOnEdges = false;
    clearTimeout(this.edgeTimer);
    this.edgeTimer = setTimeout(() => {
      mousewheel.releaseOnEdges = true;
    }, STACK.wheelGap);
  }

  #unmountSwiper() {
    if (!this.swiper) return;
    this.body.removeEventListener('wheel', this.onWheel);
    this.viewObserver?.disconnect();
    clearTimeout(this.transitionTimer);
    clearTimeout(this.edgeTimer);
    this.swiper.destroy(true, false);
    this.swiper = null;
  }

  #show(index) {
    if (this.swiper) this.swiper.slideTo(index);
    this.#setActive(index);
  }

  /** The open card's tab stops: its strip button, then anything focusable in its copy. */
  #tabStops() {
    const step = this.steps[this.activeIndex];
    const toggle = this.toggles[this.activeIndex];
    const content = step?.querySelector('.step__content');
    const inner = content ? Array.from(content.querySelectorAll(FOCUSABLE)) : [];
    return [toggle, ...inner].filter(Boolean);
  }

  /**
   * Tab off the open card's last stop opens the next card, Shift+Tab off its first the
   * previous one. Past either end focus leaves normally.
   */
  #onTab(event) {
    const dir = event.shiftKey ? -1 : 1;
    const stops = this.#tabStops();
    const at = stops.indexOf(event.target);
    const atEdge = dir > 0 ? at === stops.length - 1 : at === 0;
    const next = this.activeIndex + dir;
    if (!atEdge || next < 0 || next >= this.steps.length) return;
    event.preventDefault();
    this.#show(next);
    const nextStops = this.#tabStops();
    (dir > 0 ? nextStops[0] : nextStops[nextStops.length - 1])?.focus({ preventScroll: true });
  }

  #setActive(index) {
    if (index === this.activeIndex) return;
    this.activeIndex = index;
    this.track.setAttribute('data-active-step', String(index));

    this.steps.forEach((step, i) => {
      const isOpen = i === index;
      step.classList.toggle('is-open', isOpen);
      step.classList.toggle('is-passed', i < index);
      const toggle = this.toggles[i];
      if (toggle) toggle.setAttribute('aria-expanded', String(isOpen));
    });
    this.#syncFocus();
  }

  /**
   * Tab only reaches what is visible: closed cards' copy is inert, and scroll mode
   * gives one stop per card (walked by #onTab) where click mode tabs the closed strips.
   */
  #syncFocus() {
    const collapsed = this.dataset.mode !== 'static';
    const sliding = Boolean(this.swiper);
    this.steps.forEach((step, i) => {
      const isOpen = i === this.activeIndex;
      const content = step.querySelector('.step__content');
      if (content) content.inert = collapsed && !isOpen;
      const toggle = this.toggles[i];
      if (!toggle) return;
      if (!collapsed) toggle.tabIndex = 0;
      else toggle.tabIndex = sliding === isOpen ? 0 : -1;
    });
  }
}

if (!customElements.get('step-stack')) {
  customElements.define('step-stack', StepStack);
}
